import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { 
  FileText,
  CalendarIcon,
  Search,
  Filter,
  FileDown
} from "lucide-react";

export default function MovimentosPage() {
  const [movimentos, setMovimentos] = useState([]);
  const [locais, setLocais] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    local_id: "all",
    empresa_id: "all",
    matricula: "",
    data_inicio: null,
    data_fim: null
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchMovimentos();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      const [locaisRes, empresasRes] = await Promise.all([
        axios.get(`${API}/locais`),
        axios.get(`${API}/empresas`)
      ]);
      setLocais(locaisRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchMovimentos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.local_id && filters.local_id !== "all") params.append("local_id", filters.local_id);
      if (filters.empresa_id && filters.empresa_id !== "all") params.append("empresa_id", filters.empresa_id);
      if (filters.matricula) params.append("matricula", filters.matricula);
      if (filters.data_inicio) params.append("data_inicio", format(filters.data_inicio, "yyyy-MM-dd"));
      if (filters.data_fim) params.append("data_fim", format(filters.data_fim, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/movimentos?${params.toString()}`);
      setMovimentos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar movimentos");
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (filters.local_id && filters.local_id !== "all") params.append("local_id", filters.local_id);
      if (filters.empresa_id && filters.empresa_id !== "all") params.append("empresa_id", filters.empresa_id);
      if (filters.matricula) params.append("matricula", filters.matricula);
      if (filters.data_inicio) params.append("data_inicio", format(filters.data_inicio, "yyyy-MM-dd"));
      if (filters.data_fim) params.append("data_fim", format(filters.data_fim, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/movimentos/export-pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `movimentos_${format(new Date(), "yyyy-MM-dd_HHmm")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exportado com sucesso");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      local_id: "all",
      empresa_id: "all",
      matricula: "",
      data_inicio: null,
      data_fim: null
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const headerAction = (
    <Button 
      onClick={exportPdf} 
      variant="outline" 
      disabled={exporting || movimentos.length === 0}
      data-testid="export-movimentos-pdf-btn"
    >
      <FileDown className="w-4 h-4 mr-2" />
      {exporting ? "A exportar..." : "Exportar PDF"}
    </Button>
  );

  return (
    <PageLayout title="Movimentos" subtitle="Histórico de entradas e saídas" headerAction={headerAction}>
      {/* Filters */}
      <Card className="border-[#E2E8F0] mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Local</Label>
              <Select value={filters.local_id} onValueChange={(value) => setFilters({ ...filters, local_id: value })}>
                <SelectTrigger data-testid="filter-local">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {locais.map((local) => (
                    <SelectItem key={local.id} value={local.id}>{local.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Empresa</Label>
              <Select value={filters.empresa_id} onValueChange={(value) => setFilters({ ...filters, empresa_id: value })}>
                <SelectTrigger data-testid="filter-empresa">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>{empresa.nome_empresa}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Matrícula</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  value={filters.matricula}
                  onChange={(e) => setFilters({ ...filters, matricula: e.target.value })}
                  placeholder="Pesquisar..."
                  className="pl-9"
                  data-testid="filter-matricula"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Data Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="filter-data-inicio">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.data_inicio ? format(filters.data_inicio, "dd/MM/yyyy") : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.data_inicio}
                    onSelect={(date) => setFilters({ ...filters, data_inicio: date })}
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Data Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="filter-data-fim">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.data_fim ? format(filters.data_fim, "dd/MM/yyyy") : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.data_fim}
                    onSelect={(date) => setFilters({ ...filters, data_fim: date })}
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
              <Filter className="w-4 h-4 mr-2" />
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : movimentos.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B]">Nenhum movimento encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>Entrada</TableHead>
                  <TableHead>Op. Entrada</TableHead>
                  <TableHead>Saída</TableHead>
                  <TableHead>Op. Saída</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movimentos.map((mov) => (
                  <TableRow key={mov.id} data-testid={`movimento-row-${mov.id}`}>
                    <TableCell className="font-bold tracking-wider">{mov.matricula}</TableCell>
                    <TableCell>{mov.empresa_nome}</TableCell>
                    <TableCell>{mov.local_nome}</TableCell>
                    <TableCell>{formatDateTime(mov.data_hora_entrada)}</TableCell>
                    <TableCell>{mov.operador_entrada_nome || "-"}</TableCell>
                    <TableCell>{formatDateTime(mov.data_hora_saida)}</TableCell>
                    <TableCell>{mov.operador_saida_nome || "-"}</TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant={mov.data_hora_saida ? "secondary" : "default"}
                        className={mov.data_hora_saida ? "" : "bg-[#00C853]"}
                      >
                        {mov.data_hora_saida ? "Concluído" : "Dentro"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </PageLayout>
  );
}
